import {cloneDeep, map, transform, flatten, values, some, concat, isArray, isEmpty} from 'lodash'
import {shallowEqual} from 'powtoon-commons/utils'
// @ts-expect-error not migrated to typescript
import {lib2ManifestItem} from 'powtoon-commons/models/lib2doc'
import {
  isImageHolderItem, isUserImageItem, isSoundItem, isTextItem, isVideoItem,
  isLicensedFeature, isFontItem, isAvatarItem
} from './'
import {AnyLibraryItem, SoundLibraryItem} from 'powtoon-library/common/types'
import {EMPTY_MANIFEST, ITEM_TYPES} from './consts'
import {getMergedTracksWithMaster, defaultMasters} from 'powtoon-commons/models/utils/masterObject'

import {
  Manifest,
  Masters,
  Slide,
  Track,
  Powtoon,
  AudioTrack,
  TextTrackSprite,
  TrackSprite,
  TextTrack,
  Camera,
  BackgroundColor,
  BackgroundAsset,
  BackgroundImageAsset,
  BackgroundVideoAsset,
  BackgroundUserImage,
  BackgroundUserVideo,
  ManifestItemOptions,
  VoiceOverAudioItem,
  CaptionsTrack
} from 'powtoon-versioning/src/currentSchema'
import produce from 'immer'
import {isCamera} from 'powtoon-commons/utils/camera'

type DocItem = Track
  | AudioTrack
  | TextTrack
  | TextTrackSprite
  | TrackSprite
  | Slide['background']
  | Camera
  | VoiceOverAudioItem
  | undefined | null

type DocItems =  Track |
  TextTrack |
  AudioTrack |
  BackgroundColor |
  BackgroundAsset |
  BackgroundImageAsset |
  BackgroundVideoAsset |
  BackgroundUserImage |
  BackgroundUserVideo |
  Camera

const MANIFEST_KEYS: (keyof Manifest)[] = ['assets', 'images', 'fonts', 'sounds', 'videos']

export function narrowManifestByDocItems(manifest: Manifest, docItems: DocItem[], fromLibItem = false): Manifest {
  return transform(docItems, (newManifest, docItem) => {
    if (!docItem) {
      return true
    }
    const isTrackItem = !('itemType' in docItem)

    if (isTrackItem && !isCamera(docItem)) {
      for (const soundEffectId of [docItem?.enterEffect?.soundEffectId, docItem.exitEffect?.soundEffectId]) {
        if (soundEffectId && soundEffectId in manifest.sounds) {
          newManifest.sounds[soundEffectId] = manifest.sounds[soundEffectId]
        }
      }
      return
    }
    
    if (isLicensedFeature(docItem)){
      // we might not have contentItemId with featureId , but we might do
      // this is ok if we dont have the manifest asset
      // we will send an api call to get the new valid manifest later all we need is the id
      const {featureId} = docItem
      newManifest.assets[featureId] = manifest.assets[featureId]
    }

    if (!('contentItemId' in docItem)) {
      return
    }
    const {contentItemId} = docItem
    if (isTextItem(docItem) && contentItemId in manifest.fonts) {
      newManifest.fonts[contentItemId] = manifest.fonts[contentItemId]
      return
    }
    if (isUserImageItem(docItem) && !fromLibItem && contentItemId in manifest.images) {
      newManifest.images[contentItemId] = manifest.images[contentItemId]
      return
    }
    if (isSoundItem(docItem) && contentItemId in manifest.sounds) {
      newManifest.sounds[contentItemId] = manifest.sounds[contentItemId]
      return
    }
    if (isVideoItem(docItem) && contentItemId in manifest.videos) {
      newManifest.videos[contentItemId] = manifest.videos[contentItemId]
      return
    }

    if (contentItemId in manifest.assets) {
      newManifest.assets[contentItemId] = manifest.assets[contentItemId]
    }

    if (
      'licenseSource' in docItem
        && docItem.licenseSource !== undefined
        && manifest.assets[docItem.licenseSource] !== undefined
    ) {
      newManifest.assets[docItem.licenseSource] = manifest.assets[docItem.licenseSource]
    }

    if (isImageHolderItem(docItem) && docItem.content && 'contentItemId' in docItem.content) {
      if (isUserImageItem(docItem.content) && docItem.content.contentItemId in manifest.images) {
        newManifest.images[docItem.content.contentItemId] = manifest.images[docItem.content.contentItemId]
        return
      }
      if (docItem.content.contentItemId in manifest.assets) {
        newManifest.assets[docItem.content.contentItemId] = manifest.assets[docItem.content.contentItemId]
      }
      if (docItem.content.contentItemId in manifest.videos) {
        newManifest.videos[docItem.content.contentItemId] = manifest.videos[docItem.content.contentItemId]
      }
      if (isAvatarItem(docItem.content) && docItem.content.audioTracks?.length) {
        newManifest.sounds[docItem.content.audioTracks[0].contentItemId] =
          manifest.sounds[docItem.content.audioTracks[0].contentItemId]
      }
    }
  }, cloneDeep(EMPTY_MANIFEST as Manifest))
}

export function compactManifest(document: Powtoon): Manifest {
  const oldManifest = document.manifest! // TODO(fix-types): remove this cast

  const extractedDocItems = extractDocItemsFromDocument(document)

  const docItems = [...extractedDocItems] .filter(Boolean)

  const newManifest = narrowManifestByDocItems(oldManifest, docItems)

  // add referenced scenes and templates
  const allSceneIds = flatten(map(document.slides, 'sceneId'))
  allSceneIds.push(document.templateId)
  transform(allSceneIds, (manifest, sceneId) => {
    if (sceneId && oldManifest.assets[sceneId] !== undefined) {
      manifest.assets[sceneId] = oldManifest.assets[sceneId]
    }
  }, newManifest)
  if (shallowEqual(oldManifest, newManifest, 1)) {
    return oldManifest
  }
  return newManifest
}


const extractDocItemsFromDocument = (document: Powtoon) => {
  const backgrounds = flatten(map(document.slides, 'background'))
  const camera = [document.slides.find(slide => isCamera(slide.camera))?.camera as Camera | undefined]
  
  const  {front = [], back =[]} = document?.masters?.tracks || defaultMasters.tracks
  
  const slideTracks = flatten([
    ...map(document.slides, 'tracks'),
    ...front,
    ...back
  ])

  const audioTracks = [
    ...(document.audioTracks || []),
    ...flatten(map(document.slides, 'audioTracks'))
  ].filter(Boolean)

  const captions = document.slides.flatMap(slide => {
    if (!isEmpty(slide.captions)) {
      return Object.values(slide.captions).flat()
    }
    return null
  }).filter(Boolean)

  const allTracks: DocItems[] = [...slideTracks, ...audioTracks, ...backgrounds, ...camera, ...captions].filter(
    (element): element is DocItems => {
      return !!element
    }
  )
  
  return extractDocItemsFromTracks(allTracks)
  
}

const extractDocItemsFromTracks = (tracks: DocItems[]) => {
  const allSprites: (TrackSprite | TextTrackSprite)[] = flatten(map(tracks, 'sprites') as any)
  const spriteAudioTracks = flatten(map(allSprites, 'audioTracks'))
  return [...tracks, ...spriteAudioTracks, ...allSprites].filter(Boolean)
}

type ValueType<X> = X extends object ? X[keyof X] : any

type ManifestAssets = ValueType<Manifest>

type FlattenedManifest = ValueType<ManifestAssets>

const flattenManifest = (manifest: Manifest): FlattenedManifest[] => flatten(values(manifest).map(m => values(m)))

export function getManifestForTracks(completeManifest: Manifest, tracks: DocItems[]) {
  const docItemsWithSpriteAndAudioTracks = extractDocItemsFromTracks(tracks)

  return narrowManifestByDocItems(
    completeManifest,
    docItemsWithSpriteAndAudioTracks
  )
}

export function getManifestItemsForTracks(completeManifest: Manifest, tracks: Slide['tracks']) {
  const manifest = getManifestForTracks(completeManifest, tracks)
  return flattenManifest(manifest)
}

// TODO we will need to create something like manifest2LibItem. or maybe add docItemFromManifestItem
const LIB_ITEM_MAP_KEYS = {contentItemId: 'id', originalHeight: 'height', originalWidth: 'width'} as any
export function getManifestItemToLikeLibItem(manifestItem: any) {
  return transform(manifestItem, (result: any, value, key: any) => {
    result[LIB_ITEM_MAP_KEYS[key] || key] = value
  }, {})
}

function getManifestVideoDataToLikeLibItem(manifestItem: any) {
  const {contentItemId, videoData: {originalHeight, originalWidth}} = manifestItem
  const newItem = {
    ...manifestItem,
    id: contentItemId,
    width: originalWidth,
    height: originalHeight
  }
  delete newItem.contentItemId
  return newItem
}

export function getManifestItemsForTracksLikeLibItem(manifestObject: Manifest, selectedTracks: Slide['tracks']) {
  return getManifestItemsForTracks(manifestObject, selectedTracks).map(item => getManifestItemToLikeLibItem(item))
}

export function getManifestSoundToLikeLibItem(manifestObject: Manifest, contentItemId: string) {
  const manifestItem = manifestObject.sounds[contentItemId]

  const newItem = {...manifestItem, id: contentItemId}
  delete (newItem as any).contentItemId

  return newItem as SoundLibraryItem
}


export function getManifestImageLikeLibItem(manifestObject: Manifest, contentItemId: string) {
  const isVideo = manifestObject.videos[contentItemId]
  const itemsManifest = narrowManifestByDocItems(
    manifestObject,
    [
      {itemType: isVideo ? ITEM_TYPES.VIDEO : ITEM_TYPES.IMAGE as any, contentItemId}
    ],
    contentItemId.startsWith('library-')
  )
  const itemHandler = isVideo ? getManifestVideoDataToLikeLibItem : getManifestItemToLikeLibItem
  const items = flattenManifest(itemsManifest).map(item => itemHandler(item))
  return items[0]
}

export function getManifestItemForDocItem(completeManifest: Manifest, docItem: DocItem) {
  const manifest = narrowManifestByDocItems(completeManifest, [docItem])
  return flattenManifest(manifest)[0]
}

export function getManifestForSlide(completeManifest: Manifest, slide: Slide, masters: Masters): Manifest | void {
  if (!slide) {
    return
  }

  const tracks = getMergedTracksWithMaster ({
    tracks: slide.tracks as any, // TODO(fix-types)
    masters,
    slide,
    useOverride: false
  })
  const captionTracks = flatten(Object.values(slide.captions)).filter((track): track is CaptionsTrack => Boolean(track))
  let newManifest = getManifestForTracks(completeManifest, [...tracks, ...captionTracks])

  if (slide.background && 'contentItemId' in slide.background && slide.background.contentItemId) {
    newManifest = createUpdatedManifest(newManifest, narrowManifestByDocItems(completeManifest, [slide.background]))
  }

  if (slide.camera && 'featureId' in slide.camera && slide.camera.featureId) {
    newManifest = createUpdatedManifest(newManifest, narrowManifestByDocItems(completeManifest, [slide.camera]))
  }

  if (slide.sceneId && completeManifest.assets[slide.sceneId] !== undefined) {
    newManifest.assets[slide.sceneId] = completeManifest.assets[slide.sceneId]
  }

  if (slide.audioTracks && slide.audioTracks.length > 0) {
    newManifest = createUpdatedManifest(newManifest, narrowManifestByDocItems(completeManifest, slide.audioTracks))
  }

  return newManifest
}

export function getManifestForServer(completeManifest: Manifest) {
  return {
    assets: Object.keys(completeManifest.assets),
    images: Object.keys(completeManifest.images),
    fonts: Object.keys(completeManifest.fonts),
    sounds: Object.keys(completeManifest.sounds),
    videos: Object.keys(completeManifest.videos)
  }
}

export const updateManifest = (origManifest: Manifest, manifestToAdd: Manifest) => {
  for (const key of MANIFEST_KEYS) {
    if (!(key in manifestToAdd)) {
      continue
    }
    if (key in origManifest) {
      Object.assign(origManifest[key], manifestToAdd[key])
    } else {
      (origManifest[key] as any) = manifestToAdd[key]
    }
  }
}

export const getManifestItemsByFilter =
    (manifest: Manifest, searchingFn: (item: any) => boolean) => flattenManifest(manifest).filter(searchingFn)

export const createUpdatedManifest = (baseManifest: Manifest, manifestToAdd: Manifest): Manifest =>
  produce(baseManifest, (origManifest) => {
    for (const key of MANIFEST_KEYS) {
      if (!(key in manifestToAdd)) {
        continue
      }
      if (key in origManifest) {
        Object.assign(origManifest[key], manifestToAdd[key])
      } else {
        (origManifest[key] as any) = manifestToAdd[key]
      }
    }
  })

export function isManifestDeepEqual(oldManifest: Manifest, newManifest: Manifest): boolean {

  const manifestItemTypes = Object.keys(newManifest) as (keyof Manifest)[]

  return !some(manifestItemTypes, manifestItemTypeKey => {

    const oldManifestItemsByType = oldManifest[manifestItemTypeKey]
    const newManifestItemsByType = newManifest[manifestItemTypeKey]
    const newItemTypeKeys = Object.keys(newManifestItemsByType)
    const oldItemTypeKeys = Object.keys(oldManifestItemsByType)

    if (oldItemTypeKeys.length !== newItemTypeKeys.length) {
      return true
    }

    return some(newItemTypeKeys, itemKey => oldManifestItemsByType[itemKey] !== newManifestItemsByType[itemKey])
  })
}

export type ManifestItemType = 'image' | 'video' | 'sound' | 'character' | 'avatar'

export function findUserMediaInManifest(mediaId: string, mediaType: ManifestItemType, manifest: Manifest) {
  switch (mediaType) {
    case 'image':
      return manifest.images[mediaId]
    case 'video':
      return manifest.videos[mediaId]
    case 'sound':
      return manifest.sounds[mediaId]
    case 'avatar':
    case 'character':
      return manifest.assets[mediaId]
    default:
      return null
  }
}

export function createManifestUpdateFromManifestItems(manifestItems: ManifestItemOptions[])  {
  if (!manifestItems) {
    return {}
  }
  if (!isArray(!manifestItems)){
    manifestItems = concat(manifestItems)
  }

  return transform(manifestItems, (manifestUpdate: Manifest, manifestItem: ManifestItemOptions, index) => {
    if (!manifestItem) {
      throw new Error('Missing library item.'), {libraryItems: manifestItems, index}
    }
    if (isUserImageItem(manifestItem)) {
      manifestUpdate.images[manifestItem.contentItemId] = manifestItem
    }
    else if (isFontItem(manifestItem)) {
      manifestUpdate.fonts[manifestItem.contentItemId] = manifestItem
    }
    else if (isSoundItem(manifestItem)) {
      manifestUpdate.sounds[manifestItem.contentItemId] = manifestItem
    }
    else if (isVideoItem(manifestItem)) {
      manifestUpdate.videos[manifestItem.contentItemId] = manifestItem
    }
    else {
      manifestUpdate.assets[manifestItem.contentItemId] = manifestItem
    }
  }, cloneDeep(EMPTY_MANIFEST))
}

export function createManifestUpdateFromLibraryItems(libraryItems: AnyLibraryItem[]) {
  if (!libraryItems) {
    return {}
  }
  
  libraryItems = concat(libraryItems).filter(Boolean)
  const manifestItems = libraryItems.map((libraryItem: AnyLibraryItem) => {
    return lib2ManifestItem(libraryItem)
  }).filter(Boolean)

  return createManifestUpdateFromManifestItems(manifestItems)
}
